/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  activateLineItemsUrl,
  getBillingHistoryUrl,
  getMarketplaceBillingDetailsUrl,
  getPrepaidsUrl,
} from '@modules/cloud-api/v1/urls'
import type {
  GetBillingHistoryUrlParams,
  GetMarketplaceBillingDetailsUrlParams,
  GetPrepaidsUrlParams,
} from '@modules/cloud-api/v1/urls'
import type { ActivateLineItemsRequest } from '@modules/cloud-api/v1/types'
import type { TimePeriod } from '@modules/ui-types'

import asyncRequest, { resetAsyncRequest } from '@/actions/asyncRequests'
import {
  ACTIVATE_LINE_ITEMS,
  FETCH_BILLING_HISTORY,
  FETCH_MARKETPLACE_BILLING_DETAILS,
  FETCH_ORGANIZATION_PREPAIDS,
} from '@/constants/actions'

export type fetchDeploymentsCostsParams = {
  organizationId: string
  timePeriod: TimePeriod
}

export function fetchBillingHistory(params: GetBillingHistoryUrlParams) {
  const url = getBillingHistoryUrl(params)

  return asyncRequest({
    type: FETCH_BILLING_HISTORY,
    url,
    meta: { organizationId: params.organizationId },
  })
}

export function fetchOrganizationPrepaids(params: GetPrepaidsUrlParams) {
  const url = getPrepaidsUrl(params)

  return asyncRequest({
    type: FETCH_ORGANIZATION_PREPAIDS,
    url,
    meta: { organizationId: params.organizationId },
  })
}

export function activateLineItems(payload: ActivateLineItemsRequest) {
  const url = activateLineItemsUrl()
  const { organization_id } = payload

  return asyncRequest({
    type: ACTIVATE_LINE_ITEMS,
    method: `PUT`,
    url,
    meta: { organization_id },
    payload,
  })
}

export const resetActivateLineItemsRequest = () => resetAsyncRequest(ACTIVATE_LINE_ITEMS)

export function fetchMarketplaceBillingDetails(params: GetMarketplaceBillingDetailsUrlParams) {
  const url = getMarketplaceBillingDetailsUrl(params)
  const { organizationId } = params

  return asyncRequest({
    type: FETCH_MARKETPLACE_BILLING_DETAILS,
    url,
    meta: { organizationId },
    crumbs: [organizationId],
  })
}
